import type { RouteDef } from '../router';
import { ssrBodyFor, browserPath } from '../router';
import { allCountries } from '../data/prices';

/**
 * Agent-facing landing pages: /ai-agents, /mcp, /agents-earn.
 *
 * EN-only by policy (these target English GEO/buyer-intent queries). The full
 * page body is server-rendered by backend/src/util/agent-landing-body.ts, so
 * the client does NOT duplicate the content:
 *   - On a DIRECT load (the GEO case — a search/AI click), the SSR body is
 *     already in <main id="route-mount">. `prerenderMarker` matches it, so the
 *     router keeps the server HTML and `hydrate()` appends nothing.
 *   - On an in-app SPA navigation, `render()` forces a full page load so the
 *     server re-renders the SSR body. No client-side copy of the content to
 *     drift out of sync with the server.
 *
 * Meta strings mirror backend/src/util/meta.ts (the SSR source of truth) so the
 * client's setMeta() overwrite is identical to what the server already shipped.
 */

const NAV_GUARD_KEY = 'roamzy:agent-landing-nav';

function landing(path: string, metaTitle: string, metaDesc: string): RouteDef {
  return {
    path,
    routeType: 'article',
    metaTitle,
    metaDesc,
    prerenderMarker: '.agent-landing-route',
    hydrate: () => {
      // Adopt path (direct load / SSR body already present): clear the
      // SPA-navigation reload guard so a later nav here can reload cleanly.
      try { sessionStorage.removeItem(NAV_GUARD_KEY); } catch { /* private mode */ }
      return '';
    },
    render() {
      // The page body is server-rendered (backend util/agent-landing-body.ts).
      // IMPORTANT: the router calls render() again on same-path re-resolves
      // (Auth.onChange settling, popstate). If the SSR body is already in the
      // DOM, KEEP it — a `window.location.assign` here while the content is
      // present triggers a reload, whose re-resolve reloads again → an
      // infinite reload loop (only agent-landing routes hit it, since every
      // other route's render() returns real HTML). Fixed 2026-07-07.
      const ssr = ssrBodyFor('.agent-landing-route');
      if (ssr !== null) return ssr;
      // Reached only via in-app SPA navigation from another route (no SSR body
      // here yet). Force ONE full load so the server renders it. The guard
      // prevents a reload loop if the SSR body never arrives (e.g. the offline
      // app-shell served by the service worker).
      try {
        if (sessionStorage.getItem(NAV_GUARD_KEY) !== browserPath()) {
          sessionStorage.setItem(NAV_GUARD_KEY, browserPath());
          window.location.assign(browserPath());
          return '';
        }
        sessionStorage.removeItem(NAV_GUARD_KEY);
      } catch {
        window.location.assign(browserPath());
        return '';
      }
      // Already reloaded once and the SSR body still isn't here → don't loop;
      // offer a plain link instead.
      return `<section class="agent-landing-section"><div class="container">`
        + `<p><a href="${browserPath()}">Open this page</a></p></div></section>`;
    },
  };
}

export const aiAgentsRoute = landing(
  '/ai-agents',
  'eSIM for AI Agents — Buy & Manage via MCP | Roamzy',
  `The agent-native global eSIM an AI agent can buy over MCP or REST: one eSIM for ${allCountries.length} countries, per-MB in USDT/USDC, anonymous, no account. Agents earn 20% referral.`,
);

export const mcpRoute = landing(
  '/mcp-server',
  'Roamzy MCP Server — Buy a Global eSIM in Claude/Cursor',
  `An MCP server that lets Claude, Cursor, Continue, Cline, Windsurf or Zed buy and manage a global eSIM — anonymous-first, 12 tools, per-MB USDT/USDC, ${allCountries.length} countries.`,
);

export const agentsEarnRoute = landing(
  '/agents-earn',
  'Agents Earn — 20% Referral on Roamzy',
  "Roamzy is the eSIM an AI agent can buy and earn from. Every account — including an agent's anonymous one — earns 20% referral on anyone it refers, forever.",
);

export const mcpToolsRoute = landing(
  '/mcp-server/tools',
  'Roamzy MCP Tools Reference — 12 Tools, Arguments and Call Order',
  "Every Roamzy MCP tool: arguments, return shapes, the call sequence from nothing to an installed eSIM, and the errors worth handling. 12 tools, anonymous-first, no API key.",
);

// Every backend-served agent landing needs a route HERE too, or the SPA router
// falls through to `*` and paints "Page not found" over a healthy SSR body —
// which is exactly what /mcp-server/tools did from the day it shipped until
// 2026-08-17. The backend list lives in util/agent-landing-body.ts (type Slug);
// agent-landing-routes.test.ts now fails if the two drift apart.
export const agentLandingRoutes: RouteDef[] = [
  aiAgentsRoute,
  mcpRoute,
  mcpToolsRoute,
  agentsEarnRoute,
];
